import axios from 'axios'
import router from '@/router'

import JwtService from '@/services/JwtService'
import NotificationService from '@/services/NotificationService'

import { useLoadingStore } from '@/stores/loading'
import { useUserStore } from '@/stores/user'

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
    static axios
    static headers

    /**
     * @description initialize vue axios
     */
    static init() {
        axios.defaults.withCredentials = false

        let api = axios.create({
            baseURL: import.meta.env.VITE_API_BASE_URL,
            headers: {
                Accept: 'application/json',
                'Accept-Language': 'en',
            },
        })

        api.interceptors.request.use(
            (config) => {
                const token = JwtService.getToken()
                const siteUuid = import.meta.env.VITE_SITE_UUID
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                if (siteUuid && siteUuid !== null) {
                    config.headers['X-SITE-FROM'] = siteUuid
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
        api.interceptors.response.use(
            (response) => {
                if (
                    response.status === 200 ||
                    response.status === 201 ||
                    response.status === 204
                ) {
                    return Promise.resolve(response)
                } else {
                    return Promise.reject(response)
                }
            },
            async (error) => {
                if (error.response?.status) {
                    useLoadingStore().setLoading(false)
                    switch (error.response.status) {
                        case 400: {
                            return Promise.reject({ data: { message: '400' } })
                        }
                        case 401:
                            setTimeout(() => {
                                // console.log(401)
                                useUserStore().purgeAuth()
                                router.replace({
                                    path: '/login',
                                    query: {
                                        redirect: router.currentRoute.fullPath,
                                    },
                                })
                            }, 500)
                            break
                        case 403: {
                            return Promise.reject({ data: { message: '403' } })
                        }
                        case 404: {
                            return Promise.reject({ data: { message: '404' } })
                        }
                        case 422: {
                            if (
                                typeof error.response.data.errors === 'object'
                            ) {
                                error.response.data.errors = Object.values(
                                    error.response.data.errors
                                )
                            }
                            await NotificationService.notifyUserErrors(
                                [
                                    ...error.response.data.errors.flatMap(
                                        (error) => {
                                            return error
                                        }
                                    ),
                                ]
                            )

                            return Promise.reject({ data: { message: '422' } })
                        }
                        case 425: {
                            return Promise.reject({ data: { message: '425' } })
                        }
                        case 429: {
                            return Promise.reject({ data: { message: '429' } })
                            // await ErrorService.notifyUserError(error.response.data.errors)
                        }
                        case 502: {
                            setTimeout(() => {
                                router.replace({
                                    path: '/login',
                                    query: {
                                        redirect: router.currentRoute.fullPath,
                                    },
                                })
                            }, 1000)
                        }
                    }
                    return Promise.reject(error.response)
                }
            }
        )
        ApiService.axios = api

        return this
    }

    static withBearer(bearer = '') {
        ApiService.axios.interceptors.request.use(
            (config) => {
                config.headers.Authorization = `Bearer ${bearer}`
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
        return this
    }

    /**
     * @description send the GET HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    static async getAll(resource = '', params = []) {
        return ApiService.axios.get(resource, params)
    }

    /**
     * @description send the GET HTTP request
     * @param resource: string
     * @param id: string
     * @returns Promise<AxiosResponse>
     */
    static async get(resource = '', id = '', config = {}) {
        if (!ApiService.axios) {
            ApiService.init()
        }
        if (id) {
            return ApiService.axios.get(`${resource}/${id}`, config)
        } else {
            return ApiService.axios.get(`${resource}`, config)
        }
    }

    /**
     * @description set the POST HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @param config
     * @returns Promise<AxiosResponse>
     */
    static async post(resource = '', params = [], config = {}) {
        return ApiService.axios.post(`${resource}`, params, config)
    }

    /**
     * @description set the POST Form Data HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    static async postFormData(resource = '', params = []) {
        ApiService.axios.interceptors.request.use(
            (config) => {
                config.headers['Content-Type'] = 'multipart/form-data'
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
        return ApiService.axios.post(`${resource}`, params)
    }

    /**
     * @description send the UPDATE HTTP request
     * @param resource: string
     * @param slug: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    static async update(resource = '', slug = '', params = []) {
        return ApiService.axios.put(`${resource}/${slug}`, params)
    }

    /**
     * @description send the UPDATE HTTP request
     * @param resource: string
     * @param params: AxiosRequestConfig
     * @returns Promise<AxiosResponse>
     */
    static async patch(resource = '', params = []) {
        return ApiService.axios.patch(`${resource}`, params)
    }

    /**
     * @description Send the DELETE HTTP request
     * @param resource: string
     * @returns Promise<AxiosResponse>
     */
    static async delete(resource = '') {
        return ApiService.axios.delete(resource)
    }
}

export default ApiService
